<template>
  <Dialog
    v-model:visible="visible"
    modal
    :position="isMobile ? 'bottom' : 'center'"
    :draggable="false"
    :showHeader="false"
    :dismissableMask="true"
    :style="sheetStyle"
    :pt="{
      root: { class: isMobile ? '!rounded-b-none !rounded-t-3xl !m-0 !max-h-[92vh]' : '!rounded-3xl' },
      content: { class: '!p-0 !rounded-3xl' },
    }"
  >
    <div v-if="lead" class="flex flex-col max-h-[92vh]">
      <!-- Grab handle + who am I talking to -->
      <div class="flex-shrink-0 px-4 pt-3 pb-3 border-b border-gray-100">
        <div v-if="isMobile" class="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />

        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="font-bold text-gray-900 truncate">{{ lead.businessName }}</div>
            <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
              <span v-if="lead.contactName">{{ lead.contactName }}</span>
              <span v-if="lead.phone" dir="ltr">{{ lead.phone }}</span>
            </div>
            <!-- Enough context to hold the conversation without opening the lead -->
            <div class="flex items-center gap-2 text-[11px] mt-1 flex-wrap">
              <span
                v-if="lead.business"
                class="text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full"
              >
                עסק רשום · {{ lead.business.appointmentsTotal }} תורים
              </span>
              <span v-if="lead.lastContactedAt" class="text-gray-400">
                דובר {{ relativeDate(lead.lastContactedAt) }}
              </span>
              <span v-else class="text-gray-400">לא דובר מעולם</span>
            </div>
            <div v-if="lastNote" class="text-[11px] text-gray-500 mt-1 line-clamp-2 bg-gray-50 rounded-lg px-2 py-1">
              {{ lastNote }}
            </div>
          </div>
          <Button icon="pi pi-times" text rounded size="small" class="!text-gray-400 flex-shrink-0" @click="close" />
        </div>

        <!-- Reach them without leaving the sheet -->
        <div v-if="lead.phone" class="flex gap-2 mt-3">
          <a
            :href="`tel:${toDialable(lead.phone)}`"
            class="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-blue-600 text-white text-sm font-medium active:bg-blue-700"
            @click="onCallStarted"
          >
            <i class="pi pi-phone text-sm" />
            חייג
          </a>
          <a
            :href="`https://wa.me/${toWhatsappNumber(lead.phone)}`"
            target="_blank"
            rel="noopener"
            class="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-green-600 text-white text-sm font-medium active:bg-green-700"
            @click="onWhatsappStarted"
          >
            <i class="pi pi-whatsapp text-sm" />
            וואטסאפ
          </a>
        </div>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        <!-- Outcome -->
        <section>
          <label class="text-xs font-semibold text-gray-500 block mb-2">איך הלך?</label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="o of outcomeOptions"
              :key="o.value"
              type="button"
              class="flex items-center justify-center gap-1.5 h-11 rounded-xl border text-sm font-medium transition-colors"
              :class="
                form.outcome === o.value
                  ? OUTCOME_CHIP_CLASSES[o.value] + ' ring-2 ring-offset-1 ring-primary-400'
                  : 'bg-white border-gray-200 text-gray-600'
              "
              @click="selectOutcome(o.value)"
            >
              <i :class="['pi', OUTCOME_ICONS[o.value], 'text-xs']" />
              {{ o.label }}
            </button>
          </div>
        </section>

        <!-- What was said -->
        <section>
          <label class="text-xs font-semibold text-gray-500 block mb-2">על מה דיברתם?</label>
          <Textarea
            ref="noteInput"
            v-model="form.note"
            rows="3"
            autoResize
            class="w-full !text-sm"
            placeholder="נקודות מהשיחה..."
          />
        </section>

        <!-- Status -->
        <section>
          <label class="text-xs font-semibold text-gray-500 block mb-2">סטטוס</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="s of STAGE_OPTIONS"
              :key="s.value"
              type="button"
              class="flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-medium transition-colors"
              :class="
                form.stage === s.value
                  ? STAGE_CHIP_CLASSES[s.value] + ' ring-2 ring-offset-1 ring-primary-400'
                  : 'bg-white border-gray-200 text-gray-500'
              "
              @click="form.stage = s.value"
            >
              <i :class="['pi', STAGE_ICONS[s.value], 'text-[10px]']" />
              {{ s.label }}
            </button>
          </div>
        </section>

        <!-- What they are waiting for -->
        <section>
          <label class="text-xs font-semibold text-gray-500 block mb-2">מחכה לפיצ'ר</label>
          <div class="flex flex-wrap gap-2 mb-2">
            <button
              v-for="f of featureChoices"
              :key="f"
              type="button"
              class="px-3 h-8 rounded-full border text-xs transition-colors"
              :class="
                form.requestedFeatures.includes(f)
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600'
              "
              @click="toggleFeature(f)"
            >
              {{ f }}
            </button>
          </div>
          <div class="flex gap-2">
            <InputText
              v-model="newFeature"
              placeholder="פיצ'ר אחר..."
              class="!text-sm flex-1 !h-10"
              @keydown.enter.prevent="addFeature"
            />
            <Button
              icon="pi pi-plus"
              class="!w-10 !h-10 !bg-gray-100 !border-gray-200 !text-gray-600 !rounded-xl"
              :disabled="!newFeature.trim()"
              @click="addFeature"
            />
          </div>
        </section>

        <!-- Task for myself -->
        <section class="bg-gray-50 rounded-2xl p-3">
          <div class="flex items-center justify-between">
            <label for="quicklog-task" class="text-xs font-semibold text-gray-600 select-none cursor-pointer">
              תזכיר לי לחזור לזה
            </label>
            <ToggleSwitch v-model="form.withTask" inputId="quicklog-task" />
          </div>

          <div v-if="form.withTask" class="mt-3 space-y-2">
            <InputText v-model="form.taskTitle" placeholder="מה לעשות?" class="!text-sm w-full !h-10" />
            <div class="flex flex-wrap gap-2">
              <button
                v-for="p of taskPresets"
                :key="p.days"
                type="button"
                class="px-3 h-9 rounded-full border text-xs font-medium transition-colors"
                :class="
                  form.taskDays === p.days
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600'
                "
                @click="setTaskPreset(p.days)"
              >
                {{ p.label }}
              </button>
            </div>
            <div class="flex gap-2">
              <input
                v-model="form.taskDate"
                type="date"
                class="flex-1 border border-gray-200 rounded-xl px-3 h-10 text-sm bg-white"
                @change="form.taskDays = null"
              />
              <input
                v-model="form.taskTime"
                type="time"
                class="w-28 border border-gray-200 rounded-xl px-3 h-10 text-sm bg-white"
              />
            </div>
          </div>
        </section>
      </div>

      <!-- Save -->
      <div
        class="flex-shrink-0 border-t border-gray-100 p-3 bg-white flex gap-2"
        :style="{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }"
      >
        <Button
          :label="nextLabel || 'שמור'"
          icon="pi pi-check"
          class="flex-1 !h-12 !bg-gradient-to-r !from-purple-600 !to-indigo-600 !text-white !border-0 !rounded-xl !font-semibold"
          :loading="saving"
          :disabled="!canSave"
          @click="save"
        />
        <Button
          v-if="nextLabel"
          label="דלג"
          outlined
          class="!h-12 !rounded-xl !px-4"
          :disabled="saving"
          @click="emit('skip')"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import type {
  IPlatformFeaturePopularity,
  IPlatformLeadLogDto,
  IPlatformLeadWithStats,
} from '@torim-app/types';
import {
  PlatformLeadActivityTypeEnum,
  PlatformLeadOutcomeEnum,
  PlatformLeadStageEnum,
} from '@torim-app/types';
import { CrmApi } from '../../api/crm-api';
import { useIsMobile } from '../../composables/useIsMobile';
import { useNotify } from '../../composables/useNotify';
import {
  OUTCOME_CHIP_CLASSES,
  OUTCOME_ICONS,
  OUTCOME_LABELS,
  STAGE_CHIP_CLASSES,
  STAGE_ICONS,
  STAGE_OPTIONS,
  relativeDate,
  toDialable,
  toWhatsappNumber,
} from '../../composables/useLeadDisplay';

const props = defineProps<{
  lead: IPlatformLeadWithStats | null;
  popularFeatures?: IPlatformFeaturePopularity[];
  /** Set in focus mode to turn the primary button into "save and move on" */
  nextLabel?: string;
}>();

const emit = defineEmits<{
  (e: 'saved', lead: IPlatformLeadWithStats): void;
  (e: 'skip'): void;
  (e: 'close'): void;
}>();

const { isMobile } = useIsMobile();
const { notifyError } = useNotify();

const visible = ref(false);
const saving = ref(false);
const newFeature = ref('');
const noteInput = ref<{ $el?: HTMLElement } | null>(null);
/** Contact type is inferred from how the operator reached out, defaulting to a call */
const contactType = ref<PlatformLeadActivityTypeEnum>(PlatformLeadActivityTypeEnum.Call);

const emptyForm = () => ({
  outcome: null as PlatformLeadOutcomeEnum | null,
  note: '',
  stage: PlatformLeadStageEnum.InProgress,
  requestedFeatures: [] as string[],
  withTask: false,
  taskTitle: '',
  taskDays: null as number | null,
  taskDate: '',
  taskTime: '09:00',
});

const form = ref(emptyForm());

const sheetStyle = computed(() =>
  isMobile.value ? { width: '100vw', margin: '0' } : { width: 'min(520px, 92vw)' },
);

const outcomeOptions = Object.values(PlatformLeadOutcomeEnum).map((value) => ({
  value,
  label: OUTCOME_LABELS[value],
}));

const taskPresets = [
  { days: 1, label: 'מחר' },
  { days: 3, label: '3 ימים' },
  { days: 7, label: 'שבוע' },
  { days: 30, label: 'חודש' },
];

/** Popular features first, then anything already on this lead */
const featureChoices = computed(() => {
  const popular = (props.popularFeatures || []).slice(0, 8).map((f) => f.feature);
  return [...new Set([...popular, ...form.value.requestedFeatures])];
});

/** Last thing that was said, so the operator opens the call already in context */
const lastNote = computed(() => {
  const notes = (props.lead?.notes || []).filter((n) => n.type !== 'system' && n.text?.trim());
  if (!notes.length) return '';
  return notes[notes.length - 1].text;
});

const canSave = computed(
  () =>
    !!form.value.outcome ||
    !!form.value.note.trim() ||
    form.value.stage !== props.lead?.stage ||
    (form.value.withTask && !!form.value.taskTitle.trim()),
);

watch(
  () => props.lead,
  (lead) => {
    visible.value = !!lead;
    if (!lead) return;
    form.value = { ...emptyForm(), stage: lead.stage, requestedFeatures: [...(lead.requestedFeatures || [])] };
    contactType.value = PlatformLeadActivityTypeEnum.Call;
  },
  { immediate: true },
);

watch(visible, (val) => {
  if (!val) emit('close');
});

const setTaskPreset = (days: number) => {
  form.value.taskDays = days;
  form.value.taskDate = DateTime.local().plus({ days }).toFormat('yyyy-MM-dd');
};

/**
 * Picking an outcome fills in what almost always follows it, so the common
 * cases ("no answer, try again in a couple of days") are a single tap.
 */
const selectOutcome = async (outcome: PlatformLeadOutcomeEnum) => {
  form.value.outcome = outcome;

  switch (outcome) {
    case PlatformLeadOutcomeEnum.Answered:
      form.value.stage = PlatformLeadStageEnum.InProgress;
      await nextTick();
      noteInput.value?.$el?.focus?.();
      break;
    case PlatformLeadOutcomeEnum.NoAnswer:
      form.value.stage = props.lead?.stage || PlatformLeadStageEnum.InProgress;
      form.value.withTask = true;
      form.value.taskTitle = form.value.taskTitle || `לנסות שוב: ${props.lead?.businessName || ''}`.trim();
      setTaskPreset(2);
      break;
    case PlatformLeadOutcomeEnum.WrongNumber:
      form.value.stage = PlatformLeadStageEnum.Cold;
      form.value.withTask = false;
      break;
    case PlatformLeadOutcomeEnum.CallbackLater:
      form.value.stage = PlatformLeadStageEnum.InProgress;
      form.value.withTask = true;
      form.value.taskTitle = form.value.taskTitle || `לחזור אל ${props.lead?.businessName || ''}`.trim();
      setTaskPreset(3);
      break;
  }
};

const onCallStarted = () => {
  contactType.value = PlatformLeadActivityTypeEnum.Call;
};

const onWhatsappStarted = () => {
  contactType.value = PlatformLeadActivityTypeEnum.Whatsapp;
  if (!form.value.outcome) form.value.stage = PlatformLeadStageEnum.InProgress;
};

const toggleFeature = (feature: string) => {
  const list = form.value.requestedFeatures;
  const idx = list.indexOf(feature);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(feature);
};

const addFeature = () => {
  const value = newFeature.value.trim();
  if (!value) return;
  if (!form.value.requestedFeatures.includes(value)) form.value.requestedFeatures.push(value);
  newFeature.value = '';
};

const buildTask = (): IPlatformLeadLogDto['task'] => {
  if (!form.value.withTask || !form.value.taskTitle.trim()) return undefined;
  const date = form.value.taskDate || DateTime.local().plus({ days: 1 }).toFormat('yyyy-MM-dd');
  const dueDate = DateTime.fromISO(`${date}T${form.value.taskTime || '09:00'}`);
  if (!dueDate.isValid) return undefined;
  return { title: form.value.taskTitle.trim(), dueDate: dueDate.toISO() as string };
};

const save = async () => {
  if (!props.lead?._id || !canSave.value) return;
  saving.value = true;
  try {
    const updated = await CrmApi.logInteraction(props.lead._id, {
      note: form.value.note.trim() || undefined,
      type: contactType.value,
      outcome: form.value.outcome || undefined,
      stage: form.value.stage,
      requestedFeatures: form.value.requestedFeatures,
      // Keep whoever already owns it; the signed-in email is not a roster value
      assignedTo: props.lead.assignedTo || undefined,
      task: buildTask(),
    });
    emit('saved', updated);
  } catch (e) {
    console.error('Failed to log interaction:', e);
    notifyError('שמירת הסיכום נכשלה');
  } finally {
    saving.value = false;
  }
};

const close = () => {
  visible.value = false;
};
</script>
